import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { AlertCircle, ShieldCheck } from 'lucide-react';
import { mfaApi } from '../../services/api';

/**
 * Inline step shown on LoginPage / MagicReturnPage / OAuthReturnPage when
 * the backend responds with `mfa_required: true`. Collects a 6-digit code
 * OR a recovery code, exchanges the challenge_token for a real session.
 *
 * The parent provides:
 *   - challengeToken: opaque, 5-min TTL
 *   - onSuccess(accessToken): called with the final JWT
 */
const MfaChallengeStep = ({ challengeToken, onSuccess, onCancel }) => {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await mfaApi.challenge(challengeToken, code.replace(/\s/g, ''));
      onSuccess(data.access_token);
    } catch (err) {
      setError(err.response?.data?.detail || t('auth.errors.invalidMfaCode'));
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="flex items-center gap-2 text-gray-300">
        <ShieldCheck className="w-5 h-5 text-amber-400" />
        <span className="text-sm">{t('auth.security.mfa.challengePrompt')}</span>
      </div>

      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
          <span className="text-red-400 text-sm">{error}</span>
        </div>
      )}

      <div>
        <Label htmlFor="mfa-challenge-code" className="text-gray-300">
          {useRecovery ? t('auth.security.mfa.recoveryCodeLabel') : t('auth.security.mfa.codeLabel')}
        </Label>
        <Input
          id="mfa-challenge-code"
          autoFocus
          required
          autoComplete="one-time-code"
          inputMode={useRecovery ? 'text' : 'numeric'}
          value={code}
          onChange={(e) => setCode(useRecovery ? e.target.value.toUpperCase() : e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder={useRecovery ? 'XXXXXXXXXX' : '123456'}
          className="mt-2 bg-zinc-900 border-zinc-800 text-white text-center tracking-widest"
        />
      </div>

      <button
        type="button"
        onClick={() => { setUseRecovery((v) => !v); setCode(''); }}
        className="text-sm text-amber-400 hover:text-amber-300 underline"
      >
        {useRecovery ? t('auth.security.mfa.useAuthenticatorInstead') : t('auth.security.mfa.useRecoveryInstead')}
      </button>

      <div className="flex justify-end gap-2 pt-2">
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel} disabled={loading}>
            {t('common.cancel')}
          </Button>
        )}
        <Button type="submit" disabled={!code || loading}
                className="bg-amber-500 hover:bg-amber-400 text-black">
          {loading ? t('common.loading') : t('auth.security.mfa.verify')}
        </Button>
      </div>
    </form>
  );
};

export default MfaChallengeStep;
